import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeVariant = 'system' | 'light' | 'dark' | 'ocean' | 'forest' | 'sunset' | 'midnight' | 'arctic' | 'volcano';

export type SeasonalTheme = 'spring' | 'summer' | 'autumn' | 'winter' | 'none';

export interface TeamColors {
  primary: string;
  secondary: string;
  accent: string;
}

export interface ThemePreferences {
  variant: ThemeVariant;
  seasonal: SeasonalTheme;
  teamColors?: TeamColors;
  autoSeasonal: boolean;
  reducedMotion: boolean;
}

interface ThemeContextType {
  theme: ThemePreferences;
  setTheme: (theme: Partial<ThemePreferences>) => void;
  applyTheme: (variant: ThemeVariant) => void;
  setTeamColors: (colors: TeamColors) => void;
  toggleAutoSeasonal: () => void;
  getCurrentSeason: () => SeasonalTheme;
  resetToDefaults: () => void;
}

const defaultTheme: ThemePreferences = {
  variant: 'system',
  seasonal: 'none',
  autoSeasonal: false,
  reducedMotion: false,
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const themeVariants = {
  system: {}, // System will use CSS media query
  light: {}, // Light will use index.css :root values  
  dark: {}, // Dark will use index.css .dark values
  ocean: {
    '--background': '200 100% 97%',
    '--foreground': '200 50% 10%',
    '--primary': '195 100% 39%',
    '--primary-foreground': '0 0% 100%',
    '--accent': '180 100% 70%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(195, 100%, 39%), hsl(180, 100%, 70%))',
  },
  forest: {
    '--background': '120 25% 95%',
    '--foreground': '120 100% 15%',
    '--primary': '120 100% 25%',
    '--primary-foreground': '0 0% 100%',
    '--accent': '80 60% 50%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(120, 100%, 25%), hsl(80, 60%, 50%))',
  },
  sunset: {
    '--background': '30 100% 98%',
    '--foreground': '30 50% 10%',
    '--primary': '15 100% 55%',
    '--primary-foreground': '0 0% 100%',
    '--accent': '45 100% 65%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(15, 100%, 55%), hsl(45, 100%, 65%))',
  },
  midnight: {
    '--background': '240 20% 8%',
    '--foreground': '240 20% 95%',
    '--primary': '260 100% 70%',
    '--primary-foreground': '240 20% 8%',
    '--accent': '280 100% 60%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(260, 100%, 70%), hsl(280, 100%, 60%))',
  },
  arctic: {
    '--background': '200 100% 99%',
    '--foreground': '200 50% 15%',
    '--primary': '200 100% 85%',
    '--primary-foreground': '200 50% 15%',
    '--accent': '190 100% 90%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(200, 100%, 85%), hsl(190, 100%, 90%))',
  },
  volcano: {
    '--background': '10 50% 5%',
    '--foreground': '10 50% 95%',
    '--primary': '5 100% 60%',
    '--primary-foreground': '0 0% 100%',
    '--accent': '20 100% 50%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(5, 100%, 60%), hsl(20, 100%, 50%))',
  },
};

const seasonalOverrides = {
  spring: {
    '--accent': '90 100% 60%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(var(--primary)), hsl(90, 100%, 60%))',
  },
  summer: {
    '--accent': '45 100% 70%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(var(--primary)), hsl(45, 100%, 70%))',
  },
  autumn: {
    '--accent': '25 100% 60%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(var(--primary)), hsl(25, 100%, 60%))',
  },
  winter: {
    '--accent': '200 100% 80%',
    '--gradient-primary': 'linear-gradient(135deg, hsl(var(--primary)), hsl(200, 100%, 80%))',
  },
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemePreferences>(defaultTheme);

  const getCurrentSeason = (): SeasonalTheme => {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'autumn';
    return 'winter';
  };

  const getSystemTheme = (): 'light' | 'dark' => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  const applyThemeToDOM = (themeConfig: ThemePreferences) => {
    const root = document.documentElement;
    
    // Handle system theme
    if (themeConfig.variant === 'system') {
      const systemTheme = getSystemTheme();
      root.classList.remove('light', 'dark');
      root.classList.add(systemTheme);
    } else if (themeConfig.variant === 'light' || themeConfig.variant === 'dark') {
      // Use index.css defined themes for light/dark
      root.classList.remove('light', 'dark');
      root.classList.add(themeConfig.variant);
    } else {
      // Apply custom theme variant
      const baseTheme = themeVariants[themeConfig.variant];
      Object.entries(baseTheme).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
      root.classList.remove('light', 'dark');
    }

    // Apply seasonal overrides if enabled
    if (themeConfig.autoSeasonal && themeConfig.seasonal !== 'none') {
      const seasonalTheme = seasonalOverrides[themeConfig.seasonal];
      Object.entries(seasonalTheme).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    }

    // Apply team colors if set
    if (themeConfig.teamColors) {
      root.style.setProperty('--primary', themeConfig.teamColors.primary);
      root.style.setProperty('--secondary', themeConfig.teamColors.secondary);
      root.style.setProperty('--accent', themeConfig.teamColors.accent);
    }

    // Apply accessibility preferences
    if (themeConfig.reducedMotion) {
      root.style.setProperty('--transition-smooth', 'none');
      root.style.setProperty('--transition-bounce', 'none');
    }

    // Update document class for theme-aware components
    root.className = root.className.replace(/theme-\w+/g, '');
    root.classList.add(`theme-${themeConfig.variant}`);
    
    if (themeConfig.seasonal !== 'none') {
      root.classList.add(`season-${themeConfig.seasonal}`);
    }
  };

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme-preferences');
    if (savedTheme) {
      try {
        const parsed = JSON.parse(savedTheme);
        
        // Auto-update seasonal theme if enabled
        if (parsed.autoSeasonal) {
          parsed.seasonal = getCurrentSeason();
        }
        
        setThemeState(parsed);
        applyThemeToDOM(parsed);
      } catch (error) {
        console.error('Failed to parse saved theme:', error);
        applyThemeToDOM(defaultTheme);
      }
    } else {
      applyThemeToDOM(defaultTheme);
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (theme.variant === 'system') {
        applyThemeToDOM(theme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [theme.variant]);

  const setTheme = (updates: Partial<ThemePreferences>) => {
    const newTheme = { ...theme, ...updates };
    
    // Auto-update seasonal if enabled
    if (newTheme.autoSeasonal) {
      newTheme.seasonal = getCurrentSeason();
    }
    
    setThemeState(newTheme);
    applyThemeToDOM(newTheme);
    localStorage.setItem('theme-preferences', JSON.stringify(newTheme));
  };

  const applyTheme = (variant: ThemeVariant) => {
    setTheme({ variant });
  };

  const setTeamColors = (colors: TeamColors) => {
    setTheme({ teamColors: colors });
  };

  const toggleAutoSeasonal = () => {
    const newAutoSeasonal = !theme.autoSeasonal;
    setTheme({
      autoSeasonal: newAutoSeasonal,
      seasonal: newAutoSeasonal ? getCurrentSeason() : 'none'
    });
  };

  const resetToDefaults = () => {
    setTheme(defaultTheme);
    localStorage.removeItem('theme-preferences');
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        applyTheme,
        setTeamColors,
        toggleAutoSeasonal,
        getCurrentSeason,
        resetToDefaults,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};